import { useQuery } from '@apollo/client/react';
import {
  Box,
  Heading,
  HStack,
  Input,
  Spinner,
  Text,
  VStack,
  Drawer,
  IconButton,
} from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import {
  HiMenu,
  HiX,
  HiHome,
  HiTemplate,
  HiCode,
  HiCog,
  HiSearch,
  HiPlus,
  HiClipboardList,
  HiCloudUpload,
} from 'react-icons/hi';
import { useLocation, Link, useNavigate } from 'react-router-dom';

import { GET_DOCUMENTS } from '#src/graphql/queries/documents';
import {
  useAppConfig,
  RECENTLY_OPENED_SIDEBAR_COUNT,
} from '#src/providers/app-config-provider';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { useSidebar } from '#src/providers/sidebar-provider';
import { getBuildInfo, type BuildInfo } from '#src/utils/build-info';
import { isValidDocumentPath } from '#src/utils/document-path';
import { formatDocumentName } from '#src/utils/format-document-name';

const navItems = [
  { path: '/', label: 'Dashboard', icon: HiHome },
  { path: '/processors', label: 'Processors', icon: HiCode },
  { path: '/prompts', label: 'Saved Prompts', icon: HiTemplate },
  { path: '/ai-audit', label: 'AI audit log', icon: HiClipboardList },
  { path: '/uploads', label: 'Uploads', icon: HiCloudUpload },
  { path: '/settings', label: 'Settings', icon: HiCog },
] as const;

const navItemsAfterDashboard = navItems.slice(1);

interface SidebarDrawerProps {
  hideFixedToggle?: boolean;
}

export function SidebarDrawer({ hideFixedToggle = false }: SidebarDrawerProps) {
  const { open, onOpen, onClose } = useSidebar();
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useAppConfig();
  const { documentPathRegex } = useAppSettings();
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const searchTrimmed = searchQuery.trim();
  const createPath = searchQuery.trim();
  const isCreatePathValid = createPath
    ? isValidDocumentPath(createPath, documentPathRegex)
    : false;

  const handleCreateClick = () => {
    if (!isCreatePathValid) return;
    navigate(`/documents/${createPath}`);
    setSearchQuery('');
    onClose();
  };

  const { data: searchData, loading: searchLoading } = useQuery(GET_DOCUMENTS, {
    variables: { search: searchTrimmed || undefined },
    skip: !searchTrimmed,
  });
  const searchResults = searchData?.documents ?? [];

  const recentDocs = (config.recentlyOpenedDocuments ?? []).slice(
    0,
    RECENTLY_OPENED_SIDEBAR_COUNT,
  );

  useEffect(() => {
    getBuildInfo().then(setBuildInfo);
  }, []);

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    return location.pathname.startsWith(path) && path !== '/';
  };

  const closeAndClearSearch = () => {
    setSearchQuery('');
    onClose();
  };

  const pos = config.sidebarTogglePosition;
  const placement =
    pos === 'top-left' || pos === 'bottom-left' ? 'start' : 'end';
  const togglePos = {
    top: pos.startsWith('top') ? 6 : undefined,
    bottom: pos.startsWith('bottom') ? 6 : undefined,
    left: pos.endsWith('left') ? 6 : undefined,
    right: pos.endsWith('right') ? 6 : undefined,
  };

  return (
    <>
      <Drawer.Root
        open={open}
        onOpenChange={(e) => {
          if ('open' in e && !e.open) {
            setSearchQuery('');
            onClose();
          }
        }}
        placement={placement}
        size="sm"
      >
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg="var(--background-base-color)">
            <Drawer.Header
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              <Heading size="md" color="blue.600">
                Note Plan
              </Heading>
              <IconButton
                aria-label="Close menu"
                variant="ghost"
                size="sm"
                onClick={onClose}
              >
                <HiX />
              </IconButton>
            </Drawer.Header>
            <Drawer.Body pt={0}>
              <VStack gap={1} align="stretch">
                {/* Dashboard */}
                {(() => {
                  const { path, label, icon: Icon } = navItems[0];
                  const active = isActive(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={onClose}
                      style={{ textDecoration: 'none' }}
                    >
                      <HStack
                        gap={3}
                        px={4}
                        py={3}
                        borderRadius="md"
                        bg={active ? 'blue.50' : 'transparent'}
                        borderWidth="1px"
                        borderColor={active ? 'blue.200' : 'transparent'}
                        _hover={{
                          bg: active ? 'blue.100' : 'bg.muted',
                        }}
                        _active={{
                          bg: active ? 'blue.200' : 'bg.emphasized',
                        }}
                      >
                        <Box
                          color={active ? 'blue.600' : 'fg.muted'}
                          fontSize="lg"
                        >
                          <Icon />
                        </Box>
                        <Text
                          fontWeight={active ? 'semibold' : 'normal'}
                          color={active ? 'blue.700' : 'fg'}
                          fontSize="md"
                        >
                          {label}
                        </Text>
                      </HStack>
                    </Link>
                  );
                })()}
                {/* Search documents */}
                <HStack px={4} pt={2} pb={1} gap={2}>
                  <Box color="fg.muted" fontSize="md">
                    <HiSearch />
                  </Box>
                  <Input
                    placeholder="Search documents…"
                    size="sm"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    variant="outline"
                  />
                  <IconButton
                    aria-label="Create document at path"
                    size="sm"
                    variant="ghost"
                    onClick={handleCreateClick}
                    disabled={!isCreatePathValid}
                  >
                    <HiPlus />
                  </IconButton>
                </HStack>
                {/* Search results or recently opened documents */}
                {searchTrimmed ? (
                  <>
                    <Text
                      px={4}
                      pt={2}
                      pb={1}
                      fontSize="xs"
                      fontWeight="semibold"
                      color="fg.muted"
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      Search results
                    </Text>
                    {searchLoading ? (
                      <HStack px={4} py={2} gap={2}>
                        <Spinner size="sm" />
                        <Text fontSize="sm" color="fg.muted">
                          Searching…
                        </Text>
                      </HStack>
                    ) : searchResults.length === 0 ? (
                      <Text px={4} py={2} fontSize="sm" color="fg.muted">
                        No documents found
                      </Text>
                    ) : (
                      searchResults.map((doc) => {
                        const docHref = `/documents/${doc.path}`;
                        const label = formatDocumentName(doc.path).join(' > ');
                        return (
                          <Link
                            key={doc.id}
                            to={docHref}
                            onClick={closeAndClearSearch}
                            style={{ textDecoration: 'none' }}
                          >
                            <HStack
                              gap={3}
                              px={4}
                              py={2}
                              borderRadius="md"
                              _hover={{ bg: 'bg.muted' }}
                              _active={{ bg: 'bg.emphasized' }}
                            >
                              <Text
                                fontWeight="normal"
                                color="fg"
                                fontSize="sm"
                                lineClamp={1}
                              >
                                {label}
                              </Text>
                            </HStack>
                          </Link>
                        );
                      })
                    )}
                  </>
                ) : (
                  recentDocs.length > 0 && (
                    <>
                      <Text
                        px={4}
                        pt={2}
                        pb={1}
                        fontSize="xs"
                        fontWeight="semibold"
                        color="fg.muted"
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        Recent
                      </Text>
                      {recentDocs.map((doc) => {
                        const docHref = `/documents/${doc.path}`;
                        const label = formatDocumentName(doc.path).join(' > ');
                        return (
                          <Link
                            key={doc.id}
                            to={docHref}
                            onClick={closeAndClearSearch}
                            style={{ textDecoration: 'none' }}
                          >
                            <HStack
                              gap={3}
                              px={4}
                              py={2}
                              borderRadius="md"
                              _hover={{ bg: 'bg.muted' }}
                              _active={{ bg: 'bg.emphasized' }}
                            >
                              <Text
                                fontWeight="normal"
                                color="fg"
                                fontSize="sm"
                                lineClamp={1}
                              >
                                {label}
                              </Text>
                            </HStack>
                          </Link>
                        );
                      })}
                    </>
                  )
                )}
                {/* Rest of nav */}
                {navItemsAfterDashboard.map(({ path, label, icon: Icon }) => {
                  const active = isActive(path);
                  return (
                    <Link
                      key={path}
                      to={path}
                      onClick={onClose}
                      style={{ textDecoration: 'none' }}
                    >
                      <HStack
                        gap={3}
                        px={4}
                        py={3}
                        borderRadius="md"
                        bg={active ? 'blue.50' : 'transparent'}
                        borderWidth="1px"
                        borderColor={active ? 'blue.200' : 'transparent'}
                        _hover={{
                          bg: active ? 'blue.100' : 'bg.muted',
                        }}
                        _active={{
                          bg: active ? 'blue.200' : 'bg.emphasized',
                        }}
                      >
                        <Box
                          color={active ? 'blue.600' : 'fg.muted'}
                          fontSize="lg"
                        >
                          <Icon />
                        </Box>
                        <Text
                          fontWeight={active ? 'semibold' : 'normal'}
                          color={active ? 'blue.700' : 'fg'}
                          fontSize="md"
                        >
                          {label}
                        </Text>
                      </HStack>
                    </Link>
                  );
                })}
              </VStack>
            </Drawer.Body>
            {buildInfo && (
              <Drawer.Footer borderTopWidth="1px" borderColor="border">
                <VStack gap={1} align="stretch" width="100%">
                  <Text fontSize="xs" color="fg.muted" fontFamily="mono">
                    Commit: {buildInfo.commit.substring(0, 7)}
                  </Text>
                  <Text fontSize="xs" color="fg.muted">
                    Built: {new Date(buildInfo.buildTime).toLocaleString()}
                  </Text>
                </VStack>
              </Drawer.Footer>
            )}
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>

      {!hideFixedToggle && (
        <Box position="fixed" zIndex={1000} {...togglePos}>
          <IconButton
            aria-label="Open navigation menu"
            onClick={onOpen}
            colorScheme="blue"
            variant="outline"
            bg="bg"
          >
            <HiMenu />
          </IconButton>
        </Box>
      )}
    </>
  );
}
